import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Activity, ShieldAlert, ShoppingCart, Package, FileText, Flame,
  AlertOctagon, Rocket, ArrowRight, Star, StarOff, GripVertical,
  CheckCircle, Clock, Circle, TrendingUp, Users, Boxes, Sparkles, ExternalLink,
  LayoutGrid, Workflow, Calendar, BarChart3,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import NAVIGATION_STRUCTURE, {
  getAllModules, getModuleStats, getSectionStats,
} from '../../config/navigation';
import ModulesMosaic from '../../components/admin/dashboard/ModulesMosaic';
import PipelineFlowLines from '../../components/admin/dashboard/PipelineFlowLines';
import CollapsibleSection from '../../components/admin/dashboard/CollapsibleSection';
import useFavorites from '../../hooks/useFavorites';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FAV_KEY = 'bmp_dashboard_favorites_cache';

/**
 * INSTITUTIONAL DASHBOARD — v2 (Harmonization Pass)
 *
 * Structure (top → bottom):
 *   A) Header (rendered by AdminLayout)
 *   B) Command Center LIVE strip (compact KPIs + CTA)
 *   C) Live Platform Status (clickable module cards)
 *   D) Quick Access (favourites, drag & drop, persisted per user)
 *   E) Recent Deliveries
 *   F) Institutional Roadmap
 *   G) Platform Statistics
 *
 * No overlap with Command Center.
 */

const STATUS_META = {
  complete: { label: 'Complete', icon: CheckCircle, ring: 'ring-emerald-300', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  in_progress: { label: 'In Progress', icon: Clock, ring: 'ring-amber-300', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  planned: { label: 'Planned', icon: Circle, ring: 'ring-slate-200', bg: 'bg-slate-50', text: 'text-slate-500', dot: 'bg-slate-400' },
};


const AdminDashboard = () => {
  const navigate = useNavigate();
  const token = useMemo(() => localStorage.getItem('admin_token'), []);
  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [kpis, setKpis] = useState(null);
  const [modules, setModules] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [roadmap, setRoadmap] = useState([]);
  const { favorites, toggle: toggleFav, persist: persistFavorites, reorder: reorderFavorites } = useFavorites();
  const [dragId, setDragId] = useState(null);

  // Persisted dashboard layout (collapsed sections + mosaic order/hidden)
  const [layout, setLayout] = useState({
    collapsed: [], mosaic_order: [], mosaic_hidden: [],
  });
  const persistLayout = useCallback(async (next) => {
    setLayout(next);
    try {
      await axios.put(`${API_URL}/api/user-preferences/dashboard-layout`,
        next, authHeader);
    } catch { /* offline retry on next change */ }
  }, [authHeader]);
  const toggleCollapsed = useCallback((sectionId) => {
    const set = new Set(layout.collapsed || []);
    set.has(sectionId) ? set.delete(sectionId) : set.add(sectionId);
    persistLayout({ ...layout, collapsed: [...set] });
  }, [layout, persistLayout]);
  const isCollapsed = (id) => (layout.collapsed || []).includes(id);

  const moduleStats = useMemo(() => getModuleStats(), []);
  const sectionStats = useMemo(() => getSectionStats(), []);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/api/command-center/kpis`, authHeader).then(r => setKpis(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/command-center/modules`, authHeader).then(r => setModules(r.data || [])).catch(() => {});
    axios.get(`${API_URL}/api/command-center/deliveries?limit=12`, authHeader).then(r => setDeliveries(r.data || [])).catch(() => {});
    axios.get(`${API_URL}/api/command-center/roadmap`, authHeader).then(r => setRoadmap(r.data || [])).catch(() => {});
    axios.get(`${API_URL}/api/user-preferences/dashboard-layout`, authHeader)
      .then(r => setLayout(r.data || { collapsed: [], mosaic_order: [], mosaic_hidden: [] }))
      .catch(() => {});
  }, [authHeader, token]);

  const handleDragStart = (id) => () => setDragId(id);
  const handleDragOver = (id) => (e) => {
    e.preventDefault();
    if (!dragId || dragId === id) return;
    const from = favorites.indexOf(dragId);
    const to = favorites.indexOf(id);
    if (from === -1 || to === -1) return;
    const next = [...favorites];
    next.splice(from, 1);
    next.splice(to, 0, dragId);
    reorderFavorites(next);
  };
  const handleDrop = () => {
    setDragId(null);
    persistFavorites(favorites);
  };

  const allModules = useMemo(() => getAllModules(), []);
  const favoriteModules = useMemo(
    () => favorites.map(id => allModules.find(m => m.id === id)).filter(Boolean),
    [favorites, allModules]
  );

  return (
    <div data-testid="admin-dashboard-page">

      {/* A) MODULES MOSAIC — Cockpit 3.0 (top, dynamic) */}
      <ModulesMosaic
        layout={layout}
        onLayoutChange={persistLayout}
      />

      {/* B) PIPELINE FLOW LINES — Leads / Opps / Deals */}
      <PipelineFlowLines />

      {/* C) Quick Access — favorites (always visible) ——————————————— */}
      <Card className="mb-4" data-testid="dashboard-quick-access">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <SectionTitle inline title="Quick Access" icon={Star}
              hint="Pin from sidebar (★) · Drag to reorder · Click star to remove" />
            <span className="text-[10px] text-[#6C757D]" data-testid="dashboard-fav-count">
              {favorites.length} favorites
            </span>
          </div>

          {favoriteModules.length === 0 ? (
            <div className="py-5 text-center border-2 border-dashed border-slate-200 rounded-lg">
              <Star className="w-5 h-5 mx-auto text-[#FF6B35] mb-1" />
              <p className="text-xs text-[#6C757D]">
                No favourites yet. Hover any module in the side menu and click the
                <Star className="w-3 h-3 inline mx-1 text-[#FF6B35]" />
                to pin it here.
              </p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {favoriteModules.map((m) => (
                <div key={m.id}
                     draggable
                     onDragStart={handleDragStart(m.id)}
                     onDragOver={handleDragOver(m.id)}
                     onDrop={handleDrop}
                     data-testid={`dashboard-fav-${m.id}`}
                     className={`group flex items-center gap-1.5 pl-2 pr-1 py-1.5 border border-[#023E8A]/20 bg-[#023E8A]/5 rounded-lg hover:bg-[#023E8A]/10 text-xs cursor-move ${dragId === m.id ? 'opacity-50' : ''}`}>
                  <GripVertical className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100" />
                  <Link to={m.path} className="text-[#023E8A] font-semibold">{m.label}</Link>
                  <button onClick={() => toggleFav(m.id)}
                          data-testid={`dashboard-fav-remove-${m.id}`}
                          className="ml-1 p-1 hover:bg-red-50 rounded text-[#FF6B35] hover:text-red-500">
                    <Star className="w-3 h-3 fill-current" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* D) Command Center LIVE strip — collapsible ——————————————————— */}
      <CollapsibleSection
        id="cc-strip"
        title="Command Center · Live"
        icon={ShieldAlert}
        hint="Operational hub · compact KPIs"
        collapsed={isCollapsed('cc-strip')}
        onToggle={toggleCollapsed}
        testId="dashboard-cc-strip-collapsible"
        defaultColor="#FF6B35">
        <div className="bg-gradient-to-r from-[#0B1220] via-[#0F172A] to-[#020617]
          rounded-lg overflow-hidden">
          <div className="py-3 px-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <StripKpi label="POs Open" value={kpis?.pos_open} icon={ShoppingCart} color="text-sky-400" />
              <StripKpi label="GRNs Month" value={kpis?.grns_month} icon={Package} color="text-emerald-400" />
              <StripKpi label="Invoices" value={kpis?.invoices_pending} icon={FileText} color="text-violet-400" />
              <StripKpi label="KYC Pending" value={kpis?.kyc_pending} icon={ShieldAlert} color="text-amber-400" />
              <StripKpi label="Sanctions" value={kpis?.sanctions_hits_open} icon={Flame} color="text-rose-400" />
              <StripKpi label="Alerts" value={kpis?.active_alerts} icon={AlertOctagon} color="text-red-400" />
            </div>
            <button onClick={() => navigate('/admin/command-center')}
                    className="px-3 py-1.5 bg-[#FF6B35] hover:bg-[#E55A28] text-white rounded-lg text-xs font-semibold flex items-center gap-1.5"
                    data-testid="dashboard-open-cc-btn">
              <Rocket className="w-3.5 h-3.5" /> Open Command Center
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </CollapsibleSection>

      {/* E) Live Platform Status — collapsible ————————————————————————— */}
      <CollapsibleSection
        id="live-status"
        title="Live Platform Status"
        icon={LayoutGrid}
        hint="Click any module for the operational sub-page"
        collapsed={isCollapsed('live-status')}
        onToggle={toggleCollapsed}
        testId="dashboard-live-status-collapsible">
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
          {modules.map((m) => {
            const meta = STATUS_META[m.status] || STATUS_META.planned;
            const StatusIcon = meta.icon;
            const section = Object.values(NAVIGATION_STRUCTURE).find(s => s.code === m.id);
            const enabledCount = section ? section.modules.filter(x => x.enabled).length : 0;
            const totalCount = section ? section.modules.length : 0;
            return (
              <Link key={m.id}
                    to={`/admin/dashboard/modules/${m.id}`}
                    data-testid={`dashboard-module-${m.id}`}
                    className={`group block p-3 rounded-xl border-2 bg-white hover:shadow-md transition-all ${meta.ring} hover:-translate-y-0.5`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[11px] font-mono font-bold text-[#2B2D42]">{m.id}</span>
                  <StatusIcon className={`w-3.5 h-3.5 ${meta.text}`} />
                </div>
                <div className="text-sm font-semibold text-[#2B2D42] truncate">{m.label}</div>
                <div className="flex items-center justify-between mt-2">
                  <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${meta.bg} ${meta.text}`}>
                    {meta.label}
                  </span>
                  {totalCount > 0 && (
                    <span className="text-[10px] text-[#6C757D] font-mono">{enabledCount}/{totalCount}</span>
                  )}
                </div>
                {m.last_delivery && (
                  <div className="mt-1.5 pt-1.5 border-t border-slate-100 text-[10px] text-[#6C757D] truncate" title={m.last_delivery}>
                    → {m.last_delivery}
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      </CollapsibleSection>

      {/* F) Recent Deliveries — collapsible ————————————————————————— */}
      <CollapsibleSection
        id="deliveries"
        title="Recent Deliveries"
        icon={Sparkles}
        hint="Chronological institutional deliveries"
        collapsed={isCollapsed('deliveries')}
        onToggle={toggleCollapsed}
        testId="dashboard-deliveries-collapsible">
        <ul className="divide-y divide-slate-100 -mx-4 -mb-4">
          {deliveries.slice(0, 8).map((d, idx) => (
            <li key={idx} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors" data-testid={`dashboard-delivery-${idx}`}>
              <span className="text-[10px] font-mono text-[#6C757D] w-20 flex-shrink-0">{d.date}</span>
              <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 bg-[#023E8A]/10 text-[#023E8A] rounded flex-shrink-0">{d.module}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#2B2D42] truncate">{d.title}</div>
                <div className="text-[11px] text-[#6C757D] truncate">{d.scope}</div>
              </div>
              {d.link && (
                <Link to={d.link} className="text-[#00B4D8] hover:text-[#023E8A]" title="Open">
                  <ExternalLink className="w-3.5 h-3.5" />
                </Link>
              )}
            </li>
          ))}
          {deliveries.length === 0 && (
            <li className="px-4 py-6 text-center text-xs text-[#6C757D]">No recent deliveries recorded.</li>
          )}
        </ul>
      </CollapsibleSection>

      {/* G) Institutional Roadmap — collapsible ————————————————————— */}
      <CollapsibleSection
        id="roadmap"
        title="Institutional Roadmap"
        icon={TrendingUp}
        hint="Phase progression and upcoming backlog"
        collapsed={isCollapsed('roadmap')}
        onToggle={toggleCollapsed}
        testId="dashboard-roadmap-collapsible">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {roadmap.map((m) => {
            const meta = STATUS_META[m.status] || STATUS_META.planned;
            return (
              <Link key={m.code}
                    to={`/admin/dashboard/modules/${m.code}`}
                    data-testid={`dashboard-roadmap-${m.code}`}
                    className={`p-3 rounded-xl border ${meta.bg} ${meta.text} border-transparent hover:shadow-md transition-all`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-mono font-bold">{m.code}</span>
                  <span className={`w-2 h-2 rounded-full ${meta.dot}`}></span>
                </div>
                <div className="text-sm font-bold text-[#2B2D42]">{m.label}</div>
                <div className="text-[10px] uppercase font-semibold mt-0.5">{meta.label}</div>
                {m.roadmap && m.roadmap.length > 0 && (
                  <ul className="mt-2 pt-2 border-t border-white/40 space-y-0.5">
                    {m.roadmap.slice(0, 3).map((r, i) => (
                      <li key={i} className="text-[10px] text-[#2B2D42]/80 truncate">• {r}</li>
                    ))}
                  </ul>
                )}
              </Link>
            );
          })}
        </div>
      </CollapsibleSection>

      {/* H) Platform Statistics — collapsible ————————————————————————— */}
      <CollapsibleSection
        id="stats"
        title="Platform Statistics"
        icon={BarChart3}
        collapsed={isCollapsed('stats')}
        onToggle={toggleCollapsed}
        testId="dashboard-stats-collapsible">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Total Modules" value={moduleStats.total} icon={Boxes} color="bg-blue-500" testId="stat-total-modules" />
          <StatCard label="Active Modules" value={moduleStats.enabled} icon={CheckCircle} color="bg-emerald-500" testId="stat-active-modules" />
          <StatCard label="In Development" value={moduleStats.disabled} icon={Clock} color="bg-orange-500" testId="stat-dev-modules" />
          <StatCard label="Phases" value={sectionStats.total} icon={TrendingUp} color="bg-purple-500" testId="stat-phases" />
          <StatCard label="Complete Phases" value={sectionStats.complete} icon={CheckCircle} color="bg-cyan-500" testId="stat-complete-phases" />
        </div>
      </CollapsibleSection>
    </div>
  );
};


// ========================================== Sub-components
const StripKpi = ({ label, value, icon: Icon, color }) => (
  <div className="flex items-center gap-1.5 text-white/90">
    <Icon className={`w-3.5 h-3.5 ${color}`} />
    <span className="text-[10px] uppercase tracking-wide text-slate-400">{label}</span>
    <span className="text-sm font-bold tabular-nums">{value ?? '—'}</span>
  </div>
);

const SectionTitle = ({ title, icon: Icon, hint, inline }) => (
  <div className={`flex items-center ${inline ? '' : 'justify-between mb-3'}`}>
    <div className="flex items-center gap-2">
      {Icon && <Icon className="w-4 h-4 text-[#FF6B35]" />}
      <h3 className="text-sm font-bold uppercase tracking-wide text-[#2B2D42]">{title}</h3>
    </div>
    {hint && <span className="text-[10px] text-[#6C757D] ml-3">{hint}</span>}
  </div>
);

const StatCard = ({ label, value, icon: Icon, color, testId }) => (
  <Card className="hover:shadow-md transition-shadow" data-testid={testId}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-[#6C757D] mb-0.5">{label}</p>
          <p className="text-2xl font-bold text-[#2B2D42] tabular-nums">{value}</p>
        </div>
        <div className={`${color} p-2 rounded-lg`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default AdminDashboard;
